import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { StateView } from '@/card/record'
import { Icon, type IconName } from '@/components/Icon'
import { fetchOutstandingReceipts, fetchUnprocessedWebhooks, retryReceipt, retryReceipts } from './api'
import {
  dateText,
  Failure,
  Money,
  OrganizationPicker,
  PaymentsPage,
  QueryFailure,
  TableSkeleton,
  timeText,
} from './PaymentsShell'
import type { FiscalReceipt } from './types'

/**
 * Фискализация и эквайринг: чеки в ОФД и журнал уведомлений (ТЗ §4.4) — тикет #78,
 * вёрстка по Fiscal.dc.html.
 *
 * <h2>Почему это список долгов, а не список чеков</h2>
 *
 * «Оплачено у нас» и «зарегистрировано в ОФД» — разные факты, между ними сетевой вызов,
 * который мог не дойти. Вывести второе из первого значит считать провалившийся вызов
 * выполненным: продажа остаётся нефискализированной, а узнают об этом по штрафу через
 * год. Поэтому экран показывает именно недоставленное, а не все чеки подряд.
 *
 * <h2>Брошенные видны наравне с остальными</h2>
 *
 * Чек в статусе `abandoned` — тот, кого не поднимет ни один планировщик, и повтор «всех
 * ожидающих» его не трогает (сервер исключает его из очереди повторов). Спрятать его
 * значит спрятать единственный чек, за которым точно надо идти руками, — поэтому он
 * отмечен отдельным предупреждением над таблицей. Руками — это кнопка «Повторить» в его
 * строке: сервер повторяет брошенный чек по запросу человека один раз и, если попытка не
 * удалась, оставляет его брошенным, а не возвращает в очередь.
 *
 * <h2>Повтор одного чека</h2>
 *
 * Кнопка в строке гаснет до назначенной попытки и говорит, когда она: отступ защищает
 * ОФД, и сервер всё равно ответит `fiscal.retry_not_due`. Итог попытки читается из
 * статуса вернувшегося чека — «зарегистрирован» или «не прошло» с причиной.
 *
 * <h2>Сколько попыток осталось — не показываем</h2>
 *
 * Предел попыток — настройка сервера (`crm.fiscal.max-attempts`), в ответ она не входит.
 * Шкала «3 из 10» с придуманным знаменателем выглядела бы точной и врала бы при первой
 * смене настройки; поэтому риски — число сделанных попыток, без «из скольких».
 */
type Filter = 'all' | 'retrying' | 'failed' | 'abandoned'

const STATUS_ICON: Record<FiscalReceipt['status'], IconName> = {
  pending: 'clock',
  sent: 'clock',
  failed: 'close',
  abandoned: 'box',
  registered: 'check',
}

const STATUS_CLASS: Record<FiscalReceipt['status'], string> = {
  pending: 'badge b-info',
  sent: 'badge b-info',
  failed: 'badge b-danger',
  abandoned: 'badge',
  registered: 'badge b-success',
}

function groupOf(receipt: FiscalReceipt): Exclude<Filter, 'all'> {
  if (receipt.status === 'abandoned') return 'abandoned'
  if (receipt.status === 'failed') return 'failed'
  return 'retrying'
}

export function FiscalPage() {
  const { t, user } = useApp()
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [filter, setFilter] = useState<Filter>('all')
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()

  // Повтор — `payments.payment:update`, список — `read`. Без права на повтор кнопки нет.
  const mayRetry = can(user, 'payments.payment', 'update')

  const receipts = useQuery({
    queryKey: ['fiscal-outstanding'],
    queryFn: ({ signal }) => fetchOutstandingReceipts(signal),
    retry: false,
  })

  const webhooks = useQuery({
    queryKey: ['acquiring-unprocessed', organizationId],
    queryFn: ({ signal }) => fetchUnprocessedWebhooks(organizationId!, signal),
    enabled: Boolean(organizationId),
    retry: false,
  })

  const retry = useMutation({
    mutationFn: () => retryReceipts(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['fiscal-outstanding'] }),
  })

  const retryOne = useMutation({
    mutationFn: (receiptId: string) => retryReceipt(receiptId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['fiscal-outstanding'] }),
  })

  const all = receipts.data ?? []
  const count = (group: Exclude<Filter, 'all'>) => all.filter((receipt) => groupOf(receipt) === group).length
  const abandoned = all.filter((receipt) => receipt.status === 'abandoned')
  // Брошенные в повтор не идут — сервер их не берёт; кнопка считает только тех, кого возьмёт.
  const retryable = all.length - abandoned.length
  const needle = search.trim().toLowerCase()
  const rows = all
    .filter((receipt) => filter === 'all' || groupOf(receipt) === filter)
    .filter((receipt) =>
      !needle ||
      [receipt.fiscalNumber, receipt.total, receipt.errorMessage, receipt.id]
        .some((value) => value != null && String(value).toLowerCase().includes(needle)),
    )

  const columns = [
    t('payments.fiscal.col.receipt'),
    t('payments.fiscal.col.created'),
    t('payments.column.receiptKind'),
    t('payments.column.receiptTotal'),
    t('payments.column.receiptStatus'),
    t('payments.fiscal.col.error'),
    ...(mayRetry ? [t('payments.fiscal.col.action')] : []),
  ]

  const actions = mayRetry && receipts.data ? (
    <button
      type="button"
      className="btn btn-primary"
      onClick={() => retry.mutate()}
      disabled={retry.isPending || retryable === 0}
      title={retryable === 0 ? t('payments.fiscal.retry.nothing') : undefined}
    >
      <Icon name="refresh" />
      {t('payments.fiscal.retry', { count: retryable })}
    </button>
  ) : null

  return (
    <PaymentsPage
      titleKey="nav.fiscal"
      sub={<span className="muted t-md">{t('payments.fiscal.outstanding')}</span>}
      actions={actions}
      leadKey="payments.fiscal.hint"
    >
      {retry.error ? <Failure error={retry.error} /> : null}
      {retryOne.error ? <Failure error={retryOne.error} /> : null}
      {retryOne.data ? (
        <div className={retryOne.data.status === 'registered' ? 'alert alert-success' : 'alert alert-warning'} role="status">
          <Icon name={retryOne.data.status === 'registered' ? 'check' : 'alert'} />
          <span>
            {retryOne.data.status === 'registered'
              ? t('payments.fiscal.retryOne.registered', { receipt: retryOne.data.fiscalNumber ?? retryOne.data.id.slice(0, 8) })
              : t('payments.fiscal.retryOne.failed', {
                  status: t(`payments.receiptStatus.${retryOne.data.status}`),
                  error: retryOne.data.errorMessage ?? '—',
                })}
          </span>
        </div>
      ) : null}
      {retry.data ? (
        <div className="alert alert-success" role="status">
          <Icon name="check" />
          {/* Ноль — не ошибка вызова: у чека есть назначенное время следующей попытки,
              и дёргать ОФД чаще значит получать отказы, неотличимые от настоящих.
              Сказать это вслух дешевле, чем оставить оператора жать кнопку. */}
          <span className="fg">
            {retry.data.submitted === 0
              ? t('payments.fiscal.retry.none')
              : t('payments.fiscal.retry.done', { count: retry.data.submitted })}
          </span>
        </div>
      ) : null}

      {receipts.error ? (
        <QueryFailure error={receipts.error} onRetry={() => void receipts.refetch()} />
      ) : receipts.isLoading ? (
        <TableSkeleton columns={columns} />
      ) : all.length === 0 ? (
        <StateView
          boxed
          kind="empty"
          title={t('payments.fiscal.empty')}
          description={t('payments.fiscal.empty.text')}
        />
      ) : (
        <>
          {abandoned.length > 0 && (
            <div className="alert alert-warning" role="alert">
              <Icon name="alert" />
              <span>
                <span className="strong">{t('payments.fiscal.abandoned.title', { count: abandoned.length })}</span>{' '}
                <span className="fg">{t('payments.fiscal.abandoned.text')}</span>
              </span>
            </div>
          )}

          <div className="toolbar">
            <div className="row-wrap" role="group" aria-label={t('payments.recon.filter')} style={{ gap: 6 }}>
              <Chip on={filter === 'all'} icon="lines" label={t('payments.fiscal.chip.all')} count={all.length}
                    onClick={() => setFilter('all')} />
              <Chip on={filter === 'retrying'} icon="clock" label={t('payments.fiscal.chip.retrying')}
                    count={count('retrying')} onClick={() => setFilter('retrying')} />
              <Chip on={filter === 'failed'} icon="close" label={t('payments.fiscal.chip.failed')}
                    count={count('failed')} onClick={() => setFilter('failed')} />
              <Chip on={filter === 'abandoned'} icon="box" label={t('payments.fiscal.chip.abandoned')}
                    count={count('abandoned')} onClick={() => setFilter('abandoned')} />
            </div>
            <span className="spacer" />
            <div className="input-icon pay-search">
              <Icon name="search" />
              <label htmlFor="fiscal-search" className="sr-only">{t('payments.fiscal.search')}</label>
              <input
                id="fiscal-search"
                className="input input-sm"
                placeholder={t('payments.fiscal.search.placeholder')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>

          {rows.length === 0 ? (
            <StateView kind="empty" compact title={t('payments.recon.filtered.empty')} />
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    {columns.map((column, index) => (
                      <th key={index} className={index === 3 ? 'num' : undefined}>{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((receipt) => (
                    <ReceiptRow
                      key={receipt.id}
                      receipt={receipt}
                      mayRetry={mayRetry}
                      retrying={retryOne.isPending && retryOne.variables === receipt.id}
                      busy={retryOne.isPending}
                      onRetry={() => retryOne.mutate(receipt.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="t-sm muted">{t('payments.fiscal.footnote', { count: all.length })}</p>
        </>
      )}

      <section className="stack" style={{ gap: 10, marginTop: 10 }}>
        <div className="page-head">
          <h2 className="h2">{t('payments.acquiring.unprocessed')}</h2>
          <span className="spacer" />
          <OrganizationPicker value={organizationId} onChange={setOrganizationId} />
        </div>
        <p className="page-lead">{t('payments.acquiring.hint')}</p>

        {!organizationId ? (
          <div className="alert alert-neutral" role="status">
            <Icon name="building" />
            <span>{t('payments.organization.choose')}</span>
          </div>
        ) : webhooks.error ? (
          <QueryFailure error={webhooks.error} compact onRetry={() => void webhooks.refetch()} />
        ) : webhooks.isLoading ? (
          <TableSkeleton columns={[t('payments.column.webhookReceived'), t('payments.column.webhookEvent')]} rows={3} />
        ) : (webhooks.data ?? []).length === 0 ? (
          <StateView kind="empty" compact title={t('payments.acquiring.empty')} />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('payments.column.webhookReceived')}</th>
                  <th>{t('payments.column.webhookProvider')}</th>
                  <th>{t('payments.column.webhookEvent')}</th>
                  <th className="num">{t('payments.column.paymentAmount')}</th>
                  <th>{t('payments.column.webhookStatus')}</th>
                  <th>{t('payments.column.webhookSignature')}</th>
                </tr>
              </thead>
              <tbody>
                {(webhooks.data ?? []).map((row, index) => {
                  const received = text(row.receivedAt ?? row.received_at)
                  const signed = (row.signatureValid ?? row.signature_valid) === true
                  return (
                    <tr key={index}>
                      <td>{dateText(received)} <span className="muted">{timeText(received)}</span></td>
                      <td>{text(row.providerCode ?? row.provider_code)}</td>
                      <td className="mono">{text(row.eventType ?? row.event_type)}</td>
                      <td className="num"><Money value={text(row.amount) || null} /></td>
                      <td>{text(row.status)}</td>
                      <td>
                        {/* Проверенная подпись и непроверенная — не оттенок статуса, а
                            отдельный факт: уведомление о зачислении денег без подписи
                            это заявка на бесплатный товар. */}
                        {signed ? (
                          <span className="flag flag-success"><Icon name="check" />{t('payments.acquiring.signed')}</span>
                        ) : (
                          <span className="flag flag-danger"><Icon name="alert" />{t('payments.acquiring.unsigned')}</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </PaymentsPage>
  )
}

function Chip({ on, icon, label, count, onClick }: {
  on: boolean; icon: IconName; label: string; count: number; onClick: () => void
}) {
  return (
    <button type="button" className="chip" aria-pressed={on} onClick={onClick}>
      <Icon name={icon} />
      {label} <span className="count">{count}</span>
    </button>
  )
}

function ReceiptRow({ receipt, mayRetry, retrying, busy, onRetry }: {
  receipt: FiscalReceipt
  mayRetry: boolean
  retrying: boolean
  busy: boolean
  onRetry: () => void
}) {
  const { t, user } = useApp()
  const lost = receipt.status === 'abandoned'
  // Брошенный повторяется по запросу человека; остальные — когда наступила их попытка.
  const notDue = !lost && receipt.nextAttemptAt != null && new Date(receipt.nextAttemptAt).getTime() > Date.now()
  // Риски — сделанные попытки; потолок отрисовки, чтобы десяток попыток не растянул ячейку.
  const pips = Math.min(receipt.attempts, 12)
  return (
    <tr className={lost ? 'is-muted' : undefined}>
      <td>
        <span className="stack">
          <span className="mono">{receipt.fiscalNumber ?? `№ ${receipt.id.slice(0, 8)}`}</span>
          {can(user, 'payments.payment', 'read') && (
            <Link to={`/payments/${receipt.paymentId}`} className="t-xs">{t('payments.fiscal.payment')}</Link>
          )}
        </span>
      </td>
      <td>
        <span className="stack">
          <span>{dateText(receipt.createdAt)}</span>
          <span className="t-xs muted">{timeText(receipt.createdAt)}</span>
        </span>
      </td>
      <td>{t(`payments.receiptKind.${receipt.kind}`)}</td>
      <td className="num"><Money value={receipt.total} /></td>
      <td className="wrap pay-status">
        <span className="status-cell">
          <span className={STATUS_CLASS[receipt.status]}>
            <Icon name={STATUS_ICON[receipt.status]} />
            {t(`payments.receiptStatus.${receipt.status}`)}
          </span>
          <span className="d pay-attempts">
            {pips > 0 && (
              <span className={lost ? 'attempts is-lost' : 'attempts'} aria-hidden="true">
                {Array.from({ length: pips }, (_, index) => <i key={index} className="on" />)}
              </span>
            )}
            <span>
              {t('payments.fiscal.attempts', { count: receipt.attempts })}
              {receipt.nextAttemptAt && !lost && (
                <> · {t('payments.fiscal.next', { at: `${dateText(receipt.nextAttemptAt)} ${timeText(receipt.nextAttemptAt)}` })}</>
              )}
              {lost && <> · {t('payments.fiscal.noMoreRetries')}</>}
            </span>
          </span>
        </span>
      </td>
      <td className="wrap" style={{ maxWidth: 260 }}>
        <span className="t-sm">{receipt.errorMessage ?? '—'}</span>
      </td>
      {mayRetry && (
        <td>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onRetry}
            disabled={busy || notDue}
            aria-label={t('payments.fiscal.retryOne.label', { receipt: receipt.fiscalNumber ?? receipt.id.slice(0, 8) })}
            title={notDue && receipt.nextAttemptAt
              ? t('payments.fiscal.retryOne.notDue', { at: `${dateText(receipt.nextAttemptAt)} ${timeText(receipt.nextAttemptAt)}` })
              : undefined}
          >
            {retrying ? t('payments.fiscal.retryOne.busy') : t('payments.fiscal.retryOne')}
          </button>
        </td>
      )}
    </tr>
  )
}

/**
 * Значение колонки витрины как текст.
 *
 * Витрина эквайринга отдаётся свободной записью — состав колонок принадлежит ей, а не
 * клиенту. Пустое значение печатается прочерком, а не «undefined»: последнее выглядит
 * как поломка там, где данных просто нет.
 */
function text(value: unknown): string {
  if (value == null || value === undefined) return ''
  return String(value)
}
